import React, { useId } from 'react'

export default function Input({
  label,
  error,
  hint,
  className = '',
  as: Tag = 'input',
  id,
  ...props
}) {
  // A visible label is only a label to assistive tech if it is *associated*
  // with the field. Callers rarely pass an id, so generate a stable one and
  // wire label/hint/error to it — otherwise a screen reader announces the
  // field as blank (WCAG 1.3.1, 3.3.2).
  const generatedId = useId()
  const fieldId = id || generatedId
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`

  const showHint = hint && !error
  const describedBy = [showHint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={fieldId} className="text-sm font-medium text-gray-700">
          {label}
          {props.required && (
            <span className="text-red-500 ml-0.5" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      <Tag
        id={fieldId}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy || undefined}
        className={`
          w-full rounded-lg border px-3 py-2 text-sm text-gray-900
          placeholder:text-gray-500 bg-white
          focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
          disabled:bg-gray-50 disabled:text-gray-500
          ${error ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'}
        `}
        {...props}
      />
      {showHint && (
        <p id={hintId} className="text-xs text-gray-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
